/**
 * Time as a frame index, and nothing else.
 *
 * Every other clock in this project is anchored to something outside itself:
 * `MusicClock` to the audio context, `PlaybackClock` to elapsed wall time. Both
 * are correct for watching something happen. Neither can be used to *make* a
 * film, because both answer "what time is it" — and an offline render needs to
 * answer "what time is frame 4271", which has one answer forever.
 *
 * So the only state here is an integer. `currentTime` is derived, never
 * accumulated: summing `1/60` four thousand times drifts by a visible amount in
 * float, and two machines that drifted differently would produce different
 * films from the same input.
 */

export const SUPPORTED_FPS = [24, 30, 60, 120] as const;
export type SupportedFps = (typeof SUPPORTED_FPS)[number];

export function isSupportedFps(value: number): value is SupportedFps {
  return (SUPPORTED_FPS as readonly number[]).includes(value);
}

export class OfflineRenderClock {
  private index = 0;

  readonly fps: number;
  readonly durationSeconds: number;
  readonly totalFrames: number;

  constructor(options: { readonly fps: number; readonly durationSeconds: number }) {
    this.fps = options.fps > 0 ? options.fps : 60;
    this.durationSeconds = Math.max(0, options.durationSeconds);
    // Inclusive of the final frame, so a one-second 60 fps render is 61 frames:
    // frame 0 at t=0 through frame 60 at t=1.
    this.totalFrames = Math.max(1, Math.floor(this.durationSeconds * this.fps) + 1);
  }

  get frameIndex(): number {
    return this.index;
  }

  /** Derived from the index. Never accumulated. */
  get currentTime(): number {
    return this.index / this.fps;
  }

  /** The time the previous frame stood at; negative before the first frame. */
  get previousTime(): number {
    return (this.index - 1) / this.fps;
  }

  /** Constant, by construction. This is what makes the render deterministic. */
  get deltaTime(): number {
    return 1 / this.fps;
  }

  get finished(): boolean {
    return this.index >= this.totalFrames - 1;
  }

  get progress(): number {
    return this.totalFrames <= 1 ? 1 : this.index / (this.totalFrames - 1);
  }

  /** Moves to the next frame. Returns false once the last one has been reached. */
  advance(): boolean {
    if (this.finished) return false;
    this.index += 1;
    return true;
  }

  seekToFrame(frameIndex: number): void {
    const clamped = Math.max(0, Math.min(this.totalFrames - 1, Math.floor(frameIndex)));
    this.index = clamped;
  }

  /** Nearest frame to a moment in song time. */
  seekToTime(seconds: number): void {
    this.seekToFrame(Math.round(seconds * this.fps));
  }

  reset(): void {
    this.index = 0;
  }
}
